// src/lib/stores/agents.svelte.ts
import type { AppEvent, QueuedTask, RunningAgent } from '../types.ts';
import { toast } from './toast.svelte.ts';

export const agentStore = $state({
  running:        [] as RunningAgent[],
  queued:         [] as QueuedTask[],
  maxParallel:    4,
  outputs:        {} as Record<string, string[]>, // agent_id → array of output chunks
  selectedAgentId: null as string | null,         // for AgentDrawer snoop
  completedToday: 0,                              // resets on page reload
  completedByHuman: 0,                            // manual status-to-done changes; resets on page reload
  failedToday:    0,
});

/** Process an incoming AppEvent from the WebSocket and update agentStore. */
export function applyEvent(event: AppEvent): void {
  switch (event.event_type) {
    case 'task_queued': {
      const payload = event.payload as { prompt?: string };
      if (!agentStore.queued.find(q => q.agent_id === event.agent_id)) {
        agentStore.queued.push({
          agent_id: event.agent_id,
          prompt: payload.prompt ?? '',
          addon_id: null,
          sort_key: Date.now(),
          created_at: event.created_at,
        });
      }
      break;
    }
    case 'task_started': {
      const payload = event.payload as { worktree_path?: string; tier?: string };
      // Capture prompt before removing from queued
      const queuedTask = agentStore.queued.find(q => q.agent_id === event.agent_id);
      agentStore.queued = agentStore.queued.filter(q => q.agent_id !== event.agent_id);
      if (!agentStore.running.find(r => r.agent_id === event.agent_id)) {
        agentStore.running.push({
          agent_id: event.agent_id,
          prompt: queuedTask?.prompt ?? '',
          worktree_path: payload.worktree_path ?? null,
          started_at: event.created_at,
          tier: payload.tier ?? null,
        });
      }
      break;
    }
    case 'task_completed':
    case 'task_failed':
    case 'task_cancelled': {
      // Capture the task's prompt before we drop it — used in the toast
      // body so the user can tell which agent finished.
      const running = agentStore.running.find(r => r.agent_id === event.agent_id);
      const prompt  = running?.prompt ?? '';
      const shortPrompt = prompt.length > 80 ? prompt.slice(0, 77) + '…' : prompt;

      agentStore.running = agentStore.running.filter(r => r.agent_id !== event.agent_id);
      agentStore.queued = agentStore.queued.filter(q => q.agent_id !== event.agent_id);

      // Round 7: background notification — toast + native OS notif.
      // Today we fire on every agent event regardless of workspace
      // because the server event payload doesn't yet carry workspace_id.
      // Cross-workspace routing comes when that field is added.
      if (event.event_type === 'task_completed') {
        agentStore.completedToday += 1;
        toast.push({
          title: 'Agent finished',
          body:  shortPrompt || 'Task completed successfully.',
          kind:  'success',
        });
      } else if (event.event_type === 'task_failed') {
        agentStore.failedToday += 1;
        toast.push({
          title: 'Agent failed',
          body:  shortPrompt || 'Task ended with an error.',
          kind:  'error',
        });
      } else if (event.event_type === 'task_cancelled') {
        toast.push({
          title: 'Agent cancelled',
          body:  shortPrompt || 'Task was cancelled.',
          kind:  'warning',
        });
      }
      break;
    }
    case 'agent_output': {
      const payload = event.payload as { chunk?: string };
      if (payload.chunk) {
        if (!agentStore.outputs[event.agent_id]) {
          agentStore.outputs[event.agent_id] = [];
        }
        agentStore.outputs[event.agent_id].push(payload.chunk);
      }
      break;
    }
    case 'task_priority_changed': {
      const payload = event.payload as { sort_key?: number };
      const task = agentStore.queued.find(q => q.agent_id === event.agent_id);
      if (task && payload.sort_key != null) {
        task.sort_key = payload.sort_key;
        agentStore.queued.sort((a, b) => a.sort_key - b.sort_key);
      }
      break;
    }
  }
}
